import { createRouter, createWebHistory } from 'vue-router';
import axios from '@/axiosConfig';
import LoginPage from './components/LoginPage.vue';
import MainPage from './components/MainPage.vue';
import QuickbooksPage from './components/QuickbooksPage.vue';
import HomePage from './components/HomePage.vue';
import ChartPopupTest from './components/ChartPopupTest.vue';
import LeaseSearch from './components/LeaseSearch.vue';
import ManageProperties from './components/ManageProperties.vue';
import MHomePage from './components/MHomePage.vue';
import ToolsPage from './components/ToolsPage.vue';
import DocumentScan from './components/DocumentScan.vue';
import MDocumentViewer from './components/MDocumentViewer.vue';
import PaymentViewer from './components/PaymentViewer.vue';
import PeopleViewer from './components/PeopleViewer.vue';
import NewPerson from './components/NewPerson.vue';
import ReminderDialog from './components/ReminderDialog.vue';
import MobileNotes from './components/MobileNotes.vue';
import ReportViewer from './components/ReportViewer.vue';
import ClipboardToPDF from './components/ClipboardToPDF.vue';
import AutomateLedgers from './components/AutomateLedgers.vue';
import ComingSoon from './components/ComingSoon.vue';
import MCashPosition from './components/MCashPosition.vue';
import MTenantSearch from './components/MTenantSearch.vue';
import MWorkOrders from './components/MWorkOrders.vue';
import MSmsInbox from './components/MSmsInbox.vue';
import MVoiceMessages from './components/MVoiceMessages.vue';
import LedgerEntry from './components/LedgerEntry.vue'; // Import the new component
import InsideChatTool from './components/InsideChatTool.vue'; // Import the new component
import MLockCodes from './components/MLockCodes.vue'; // Import the new component
import DownloadFiles from './components/DownloadFiles.vue'; // Import the new component
import MAppliances from './components/MAppliances.vue'; // Import the appliances component
import QuickEmail from './components/QuickEmail.vue'; // Import the quick email component
import MPropertySummary from './components/MPropertySummary.vue'; // Import the property summary component
import PropertyMapper from './components/PropertyMapper.vue'; // Import the property mapper component
import ServerManagement from './components/ServerManagement.vue'; // Import the server management component
import QRCollector from './components/QRCollector.vue'; // Import QR collector component
import CollectorSetup from './components/CollectorSetup.vue'; // Import collector setup component
import MobileSurvey from './components/MobileSurvey.vue'; // Import survey component
import SurveySetup from './components/SurveySetup.vue'; // Import survey setup component
import CalendarTool from './components/CalendarTool.vue'; // Import calendar component
import BatterySchedule from './components/BatterySchedule.vue'; // Import battery schedule component
import LeaseLedgerEntryEdit from './components/LeaseLedgerEntryEdit.vue'; // Import lease ledger entry edit component
import TenantSchedule from './components/TenantSchedule.vue'; // Tenant work order scheduling page
import RentMeAccept from './components/RentMeAccept.vue'; // Public rent-me acceptance page
import { getDefaultHomePath, isMobileDevice } from '@/utils/deviceHome';

const routes = [
  // PWA start_url is "/". Send phones to /mhome (auth gate) and desktop to /home.
  {
    path: '/',
    redirect: () => getDefaultHomePath(),
    roleCheck: 99,
  },
  { path: '/login', name: 'login', component: LoginPage, roleCheck: 99 },
  { path: '/ichat', name: 'ichat', component: InsideChatTool, roleCheck: 3 },
  { path: '/main', component: MainPage, roleCheck: 2 },
  // Auth required (viewer+). Do not treat as public — unauthenticated
  // visitors must fail closed to /login instead of mounting HomePage.
  { path: '/home', component: HomePage, roleCheck: 3 },
  { path: '/quick', component: QuickbooksPage, roleCheck: 1 },
  { path: '/test-chart', component: ChartPopupTest, roleCheck: 1 },
  { path: '/lease-search', component: LeaseSearch, roleCheck: 2 },
  { path: '/lease-ledger-edit', component: LeaseLedgerEntryEdit, roleCheck: 2 },
  { path: '/properties', component: ManageProperties, roleCheck: 2 },
  // Auth required (viewer+). Do not treat as public — invalid/missing token
  // must fail closed to /login instead of mounting a half-broken grey home shell.
  { path: '/mhome', component: MHomePage, roleCheck: 3 },
  { path: '/tools', component: ToolsPage, roleCheck: 1 },
  { path: '/documents', component: DocumentScan, roleCheck: 2 },
  { path: '/mdocs', component: MDocumentViewer, roleCheck: 3 },
  { path: '/payments', component: PaymentViewer, roleCheck: 2 },
  { path: '/people', component: PeopleViewer, roleCheck: 2 },
  { path: '/new-person', component: NewPerson, roleCheck: 2 },
  { path: '/reminder', component: ReminderDialog, roleCheck: 2 },
  { path: '/mnotes', component: MobileNotes, roleCheck: 3 },
  { path: '/reports', component: ReportViewer, roleCheck: 2 },
  { path: '/clip', component: ClipboardToPDF, roleCheck: 2 },
  { path: '/automate', component: AutomateLedgers, roleCheck: 2 },
  { path: '/nothing', component: ComingSoon, roleCheck: 2 },
  { path: '/quicklook', component: MCashPosition, roleCheck: 2 },
  { path: '/mtenantsearch', component: MTenantSearch, roleCheck: 3 },
  { path: '/mworkorders', component: MWorkOrders, roleCheck: 3 },
  { path: '/msms', component: MSmsInbox, roleCheck: 2 },
  { path: '/mvoice', component: MVoiceMessages, roleCheck: 2 },
  { path: '/ledgerentry/:id?', component: LedgerEntry, roleCheck: 2 },
  { path: '/lockcodes', component: MLockCodes, roleCheck: 3 },
  { path: '/download', component: DownloadFiles, roleCheck: 2 },
  { path: '/appliances', component: MAppliances, roleCheck: 3 }, // Add the appliances route
  { path: '/quickemail', component: QuickEmail, roleCheck: 3 }, // Add the quick email route
  { path: '/mproperties', component: MPropertySummary, roleCheck: 3 }, // Add the property summary route
  { path: '/mapit', component: PropertyMapper, roleCheck: 2 }, // Add the property mapper route
  { path: '/server-management', component: ServerManagement, roleCheck: 1 }, // Add the server management route (admin only)
  { path: '/collector', component: QRCollector, roleCheck: 99 }, // Public QR collector form
  { path: '/campaigns', component: CollectorSetup, roleCheck: 2 }, // Campaign management (manager and above)
  { path: '/survey', component: MobileSurvey, roleCheck: 99 }, // Public survey form
  { path: '/surveys', component: SurveySetup, roleCheck: 2 }, // Survey management (manager and above)
  { path: '/calendar', component: CalendarTool, roleCheck: 3 }, // Calendar view (viewer and above)
  { path: '/schedule', component: BatterySchedule, roleCheck: 99 }, // Public battery scheduling page
  { path: '/workorder-schedule', component: TenantSchedule, roleCheck: 99 }, // Public tenant WO scheduling
  { path: '/rent-me', component: RentMeAccept, roleCheck: 99 }, // Public rent-me acceptance
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

/** Resolve route config including parameterized paths like /ledgerentry/:id? */
function findRouteConfig(toPath) {
  const exact = routes.find((route) => route.path === toPath);
  if (exact) return exact;

  return routes.find((route) => {
    if (!route.path.includes(':')) return false;
    const pattern = `^${route.path
      .replace(/\?/g, '')
      .replace(/:[^/]+/g, '[^/]+')}$`;
    return new RegExp(pattern).test(toPath);
  });
}

function clearAuthSession() {
  localStorage.removeItem('token');
  delete axios.defaults.headers.common['Authorization'];
}

function setRouteLoading(isLoading) {
  try {
    window.dispatchEvent(
      new CustomEvent('pixie-route-loading', { detail: { isLoading } }),
    );
  } catch (_) {
    /* ignore */
  }
}

/**
 * Validate JWT with a hard timeout so a hung network never leaves a blank shell.
 */
async function fetchValidatedUser(token) {
  const response = await axios.get('/api/validate-token', {
    timeout: 12000,
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.data?.valid || !response.data?.user) {
    const err = new Error(
      response.data?.error || response.data?.message || 'Invalid token',
    );
    err.response = response;
    throw err;
  }
  return response.data.user;
}

// Navigation guard to check device type and user role before allowing access.
// Vue Router 4: return a value instead of calling next(value).
router.beforeEach(async (to) => {
  setRouteLoading(true);

  // Store voicememo parameter if present (for sharing feature)
  if (to.query.voicememo) {
    sessionStorage.setItem('pendingVoiceMemo', to.query.voicememo);
  }

  // Allow unrestricted access to login page
  if (to.path === '/login') {
    return true;
  }

  const targetRoute = findRouteConfig(to.path);
  const isPublicRoute = targetRoute && targetRoute.roleCheck === 99;
  console.log(
    '[ROUTER GUARD]',
    to.path,
    'targetRoute:',
    targetRoute?.path,
    'roleCheck:',
    targetRoute?.roleCheck,
    'isPublic:',
    isPublicRoute,
  );

  // Allow access to public routes without authentication
  if (isPublicRoute && to.path !== '/login') {
    return true;
  }

  try {
    const token = localStorage.getItem('token');

    if (!token) {
      sessionStorage.setItem('redirectAfterLogin', to.fullPath);
      return '/login';
    }

    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    const user = await fetchValidatedUser(token);
    console.log('User role:', user.role);

    // Check if user has permission to access the route
    if (targetRoute && user.role <= targetRoute.roleCheck) {
      if (to.path === '/home' && (isMobileDevice() || user.role > 2)) {
        return '/mhome';
      }
      return true;
    }

    // Permission denied — fall back to the device home (once), never loop
    const fallback = getDefaultHomePath();
    if (to.path === fallback || to.path === '/mhome' || to.path === '/home') {
      clearAuthSession();
      sessionStorage.setItem('redirectAfterLogin', to.fullPath);
      return '/login';
    }
    return fallback;
  } catch (error) {
    console.error('Authentication error:', error);
    clearAuthSession();
    sessionStorage.setItem('redirectAfterLogin', to.fullPath);
    return '/login';
  }
});

router.afterEach(() => {
  setRouteLoading(false);
});

router.onError(() => {
  setRouteLoading(false);
});

export default router;
